import { normalizeFolderLeafName } from './fileNameUtils';

const DOCUMENT_UPLOAD_SYSTEM_FILE_NAMES = new Set([
  '.ds_store',
  'desktop.ini',
  'thumbs.db',
  'icon\r',
]);

const DOCUMENT_UPLOAD_SYSTEM_DIRECTORY_NAMES = new Set([
  '__macosx',
  '.fseventsd',
  '.spotlight-v100',
  '.trashes',
]);

export const getDocumentUploadFilePath = (file) => String(
  file?.webkitRelativePath || file?.relativePath || file?.name || ''
).replace(/\\/g, '/');

export const getDocumentUploadFileKey = (file) => [
  getDocumentUploadFilePath(file),
  Number(file?.size || 0),
  Number(file?.lastModified || 0),
].join(':');

export const isDocumentUploadSystemArtifact = (path) => {
  const segments = String(path || '')
    .replace(/\\/g, '/')
    .split('/')
    .filter(Boolean);

  return segments.some((segment) => {
    const normalized = segment.toLowerCase();
    return DOCUMENT_UPLOAD_SYSTEM_FILE_NAMES.has(normalized)
      || DOCUMENT_UPLOAD_SYSTEM_DIRECTORY_NAMES.has(normalized)
      || normalized.startsWith('._');
  });
};

export const filterDocumentUploadFiles = (files) => Array.from(files || [])
  .filter((file) => file && !isDocumentUploadSystemArtifact(getDocumentUploadFilePath(file)));

export const mergeDocumentUploadFiles = (...fileCollections) => {
  const merged = [];
  const seen = new Set();

  fileCollections.forEach((files) => {
    filterDocumentUploadFiles(files).forEach((file) => {
      const key = getDocumentUploadFileKey(file);
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(file);
    });
  });

  return merged;
};

const normalizeDocumentUploadDestination = (destinationPath) => {
  const normalized = String(destinationPath || '')
    .replace(/\\/g, '/')
    .replace(/\/+$/g, '');
  if (!normalized || normalized === 'root') return '{{Name}}';
  return normalized;
};

export const joinDocumentUploadPath = (destinationPath, relativePath = '') => {
  const destination = normalizeDocumentUploadDestination(destinationPath);
  const relative = String(relativePath || '')
    .replace(/\\/g, '/')
    .replace(/^\/+|\/+$/g, '');
  return relative ? `${destination}/${relative}` : destination;
};

export const resolveDocumentUploadFolderPath = (destinationPath, relativeFilePath = '') => {
  const pathParts = String(relativeFilePath || '')
    .replace(/\\/g, '/')
    .split('/')
    .filter(Boolean);
  pathParts.pop();
  return joinDocumentUploadPath(destinationPath, pathParts.join('/'));
};

const normalizeRelativeDocumentFolderPath = (path) => String(path || '')
  .replace(/\\/g, '/')
  .replace(/^\/+|\/+$/g, '')
  .split('/')
  .filter(Boolean)
  .join('/');

/**
 * Build editable directory-upload rows from cumulative browser paths while
 * keeping each input scoped to one leaf name.
 */
export const buildDocumentUploadFolderRenameRows = (folderPaths) => Array.from(folderPaths || [])
  .map((folderPath) => {
    const originalPath = normalizeRelativeDocumentFolderPath(folderPath);
    const pathParts = originalPath.split('/').filter(Boolean);
    const leafName = pathParts[pathParts.length - 1] || '';

    return {
      file: null,
      originalPath,
      originalName: leafName,
      displayPath: originalPath,
      nameWithoutExt: leafName,
      extension: '',
      newExtension: '',
      newName: leafName,
      relativePath: originalPath,
    };
  })
  .filter((row) => row.originalPath && row.originalName);

/**
 * Resolve a browser-relative folder path after the user edits any folder leaf.
 * Each replacement is normalized as a single segment; structural separators
 * come only from the original directory tree.
 */
export const resolveRenamedDocumentUploadFolderPath = (relativeFolderPath, folderRows = []) => {
  const rowsByPath = new Map(
    Array.from(folderRows || []).map((row) => [
      normalizeRelativeDocumentFolderPath(row?.originalPath || row?.relativePath),
      row,
    ]),
  );
  const pathParts = normalizeRelativeDocumentFolderPath(relativeFolderPath)
    .split('/')
    .filter(Boolean);

  return pathParts.map((part, index) => {
    const originalPath = pathParts.slice(0, index + 1).join('/');
    const row = rowsByPath.get(originalPath);
    return normalizeFolderLeafName(row?.newName ?? part);
  }).join('/');
};

/**
 * Convert a case-folder key or relative path to the canonical UI form rooted
 * at the literal `{{Name}}` segment.
 */
export const normalizeDocumentCaseFolderPath = (path) => {
  let normalized = String(path || '')
    .replace(/\\/g, '/')
    .replace(/\/+$/g, '');

  if (!normalized || normalized === 'root') return '{{Name}}';

  normalized = normalized.replace(/^cases\/[^/]+\//i, '');
  normalized = normalized.replace(/^\/+/, '');
  const nameTokenIndex = normalized.indexOf('{{Name}}');
  if (nameTokenIndex >= 0) {
    normalized = normalized.slice(nameTokenIndex);
  } else {
    normalized = `{{Name}}/${normalized}`;
  }

  return normalized.replace(/\/{2,}/g, '/').replace(/\/+$/g, '') || '{{Name}}';
};

export const isDocumentFolderPathWithin = (path, possibleAncestor) => {
  const normalizedPath = normalizeDocumentCaseFolderPath(path);
  const normalizedAncestor = normalizeDocumentCaseFolderPath(possibleAncestor);
  return normalizedPath === normalizedAncestor || normalizedPath.startsWith(`${normalizedAncestor}/`);
};

export const isDocumentFolderPathStrictlyWithin = (path, possibleAncestor) => (
  isDocumentFolderPathWithin(path, possibleAncestor)
  && normalizeDocumentCaseFolderPath(path) !== normalizeDocumentCaseFolderPath(possibleAncestor)
);

export const doDocumentFolderPathsOverlap = (firstPath, secondPath) => (
  isDocumentFolderPathWithin(firstPath, secondPath)
  || isDocumentFolderPathWithin(secondPath, firstPath)
);

export const getDocumentFolderParentPath = (path) => {
  const normalizedPath = normalizeDocumentCaseFolderPath(path);
  const parts = normalizedPath.split('/').filter(Boolean);
  return parts.length > 1 ? parts.slice(0, -1).join('/') : '{{Name}}';
};

export const buildDocumentFolderChildPath = (parentPath, folderLeafName) => (
  joinDocumentUploadPath(
    normalizeDocumentCaseFolderPath(parentPath),
    normalizeFolderLeafName(folderLeafName),
  )
);

export const getDocumentFolderRenameParentPaths = (sourcePath, targetPath) => (
  Array.from(new Set([
    getDocumentFolderParentPath(sourcePath),
    getDocumentFolderParentPath(targetPath),
  ]))
);

export const getDocumentFolderRenameViewChange = ({
  activePath,
  sourcePath,
  targetPath,
  success,
}) => {
  const normalizedActivePath = normalizeDocumentCaseFolderPath(activePath);
  const fromPath = success ? sourcePath : targetPath;
  const toPath = success ? targetPath : sourcePath;
  const affected = isDocumentFolderPathWithin(normalizedActivePath, fromPath);

  return {
    affected,
    activePath: normalizedActivePath,
    nextActivePath: affected
      ? remapDocumentFolderPath(normalizedActivePath, fromPath, toPath)
      : normalizedActivePath,
  };
};

export const getActiveDocumentFolderRenames = (folderRenames, caseId) => (
  Object.values(folderRenames || {}).filter(rename => (
    rename
    && String(rename.caseId) === String(caseId)
    && !['completed', 'failed'].includes(rename.status)
  ))
);

export const getTerminalDocumentFolderRenames = (folderRenames, caseId) => (
  Object.values(folderRenames || {}).filter(rename => (
    rename
    && String(rename.caseId) === String(caseId)
    && ['completed', 'failed'].includes(rename.status)
  ))
);

export const getDocumentFolderRenameCompletionNotice = (result = {}) => {
  const job = result?.job && typeof result.job === 'object' ? result.job : {};
  const warning = job.warning || result?.warning || '';
  const warningCode = job.warning_code || result?.warning_code || '';
  const hasWarning = Boolean(warning || warningCode);
  const statusMessage = warning
    || job.message
    || result?.message
    || 'Folder rename complete';

  return {
    hasWarning,
    statusMessage,
    message: {
      type: hasWarning ? 'warning' : 'success',
      text: hasWarning ? statusMessage : 'Folder rename complete.',
    },
    ...(warning ? { warning } : {}),
    ...(warningCode ? { warningCode } : {}),
  };
};

export const findDocumentFolderRenameConflict = (
  paths,
  folderRenames,
  caseId,
  {
    includeAncestors = false,
    fencingOnly = true,
    operation = null,
    allowSourceAdditions = false,
  } = {},
) => {
  const candidates = Array.from(paths || []).filter(Boolean);
  return getActiveDocumentFolderRenames(folderRenames, caseId).find(rename => (
    (!fencingOnly || rename.fencing === true)
    &&
    candidates.some(candidatePath => (
      [
        { path: rename.sourcePath, source: true },
        { path: rename.targetPath, source: false },
      ]
        .filter(scope => Boolean(scope.path))
        .some(scope => {
          const conflicts = includeAncestors
            ? doDocumentFolderPathsOverlap(candidatePath, scope.path)
            : isDocumentFolderPathWithin(candidatePath, scope.path);
          if (!conflicts) return false;

          const sourceAdditionException = scope.source
            && allowSourceAdditions
            && ['upload', 'create_folder'].includes(operation)
            && rename.sourceAdditionsAllowed === true
            && isDocumentFolderPathStrictlyWithin(candidatePath, rename.sourcePath);
          return !sourceAdditionException;
        })
    ))
  )) || null;
};

export const remapDocumentFolderPath = (path, sourcePath, targetPath) => {
  const normalizedPath = normalizeDocumentCaseFolderPath(path);
  const normalizedSource = normalizeDocumentCaseFolderPath(sourcePath);
  const normalizedTarget = normalizeDocumentCaseFolderPath(targetPath);

  if (!isDocumentFolderPathWithin(normalizedPath, normalizedSource)) {
    return normalizedPath;
  }

  return `${normalizedTarget}${normalizedPath.slice(normalizedSource.length)}`;
};

const DOCUMENT_FOLDER_PATH_FIELDS = new Set([
  'key',
  'file_path',
  'folder_path',
  'path',
  'full_path',
  'fullPath',
  's3_key',
  'old_key',
  'new_key',
]);

const isExplicitDocumentStoragePath = (value) => {
  const rawValue = String(value || '');
  return rawValue.includes('{{Name}}')
    || /^cases\/[^/]+\//i.test(rawValue)
    || rawValue.includes('/');
};

const remapDocumentStoragePath = (path, sourcePath, targetPath) => {
  const rawPath = String(path || '');
  if (!rawPath || !isDocumentFolderPathWithin(rawPath, sourcePath)) return rawPath;

  const remappedPath = remapDocumentFolderPath(rawPath, sourcePath, targetPath);
  const nameTokenIndex = rawPath.indexOf('{{Name}}');
  const trailingSlash = rawPath.endsWith('/') ? '/' : '';

  if (nameTokenIndex >= 0) {
    return `${rawPath.slice(0, nameTokenIndex)}${remappedPath}${trailingSlash}`;
  }
  return `${remappedPath}${trailingSlash}`;
};

/**
 * Remap path-bearing fields inside cached folder/file payloads. File names and
 * unrelated scalar data are deliberately left alone.
 */
export const remapDocumentFolderPayload = (value, sourcePath, targetPath) => {
  if (Array.isArray(value)) {
    return value.map(item => remapDocumentFolderPayload(item, sourcePath, targetPath));
  }
  if (!value || typeof value !== 'object') return value;

  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return value;

  return Object.fromEntries(Object.entries(value).map(([field, fieldValue]) => {
    const nextField = (
      isExplicitDocumentStoragePath(field)
      && isDocumentFolderPathWithin(field, sourcePath)
    )
      ? remapDocumentStoragePath(field, sourcePath, targetPath)
      : field;
    if (
      DOCUMENT_FOLDER_PATH_FIELDS.has(field)
      && typeof fieldValue === 'string'
      && isDocumentFolderPathWithin(fieldValue, sourcePath)
    ) {
      return [nextField, remapDocumentStoragePath(fieldValue, sourcePath, targetPath)];
    }
    return [nextField, remapDocumentFolderPayload(fieldValue, sourcePath, targetPath)];
  }));
};

export const remapDocumentFolderRecord = (record, sourcePath, targetPath) => {
  const entries = Object.entries(record || {});
  const remapped = {};

  entries.forEach(([path, value]) => {
    if (!isDocumentFolderPathWithin(path, sourcePath)) {
      remapped[path] = remapDocumentFolderPayload(value, sourcePath, targetPath);
    }
  });
  entries.forEach(([path, value]) => {
    if (isDocumentFolderPathWithin(path, sourcePath)) {
      const nextPath = remapDocumentFolderPath(path, sourcePath, targetPath);
      const nextValue = remapDocumentFolderPayload(value, sourcePath, targetPath);
      const originalLeaf = normalizeDocumentCaseFolderPath(path).split('/').pop();
      const nextLeaf = nextPath.split('/').pop();
      remapped[nextPath] = (
        nextValue
        && !Array.isArray(nextValue)
        && typeof nextValue === 'object'
        && nextValue.name === originalLeaf
      ) ? { ...nextValue, name: nextLeaf } : nextValue;
    }
  });

  return remapped;
};

export const removeDocumentFolderRecord = (record, folderPath) => Object.fromEntries(
  Object.entries(record || {}).filter(([path]) => (
    !isDocumentFolderPathWithin(path, folderPath)
  )),
);

export const reconcileDocumentCurrentFolder = (
  currentFolder,
  sourcePath,
  targetPath,
  success,
) => {
  if (!currentFolder?.path) return currentFolder;

  const normalizedCurrentPath = normalizeDocumentCaseFolderPath(currentFolder.path);
  const normalizedSourcePath = normalizeDocumentCaseFolderPath(sourcePath);
  const normalizedTargetPath = normalizeDocumentCaseFolderPath(targetPath);
  const fromPath = success ? normalizedSourcePath : normalizedTargetPath;
  const toPath = success ? normalizedTargetPath : normalizedSourcePath;
  const currentInsideFrom = isDocumentFolderPathWithin(normalizedCurrentPath, fromPath);
  const nextCurrentPath = currentInsideFrom
    ? remapDocumentFolderPath(normalizedCurrentPath, fromPath, toPath)
    : normalizedCurrentPath;
  const subfolderEntries = Object.entries(currentFolder.subfolders || {});
  const remappedSubfolders = {};

  // Keep unaffected children first so the authoritative source child wins a
  // collision with an optimistic target child.
  [...subfolderEntries].sort(([firstName], [secondName]) => {
    const firstPath = `${normalizedCurrentPath}/${firstName}`;
    const secondPath = `${normalizedCurrentPath}/${secondName}`;
    return Number(isDocumentFolderPathWithin(firstPath, fromPath))
      - Number(isDocumentFolderPathWithin(secondPath, fromPath));
  }).forEach(([childName, childValue]) => {
    const childPath = normalizeDocumentCaseFolderPath(`${normalizedCurrentPath}/${childName}`);
    const childInsideFrom = isDocumentFolderPathWithin(childPath, fromPath);

    // A failed rename leaves the source child authoritative. If the active
    // view is its parent, remove an optimistic target sibling instead of
    // renaming it over the existing source entry.
    if (!success && !currentInsideFrom && childInsideFrom) return;

    const nextChildPath = childInsideFrom
      ? remapDocumentFolderPath(childPath, fromPath, toPath)
      : childPath;
    const nextChildName = nextChildPath.startsWith(`${nextCurrentPath}/`)
      ? nextChildPath.slice(nextCurrentPath.length + 1).split('/')[0]
      : childName;
    let nextChildValue = remapDocumentFolderPayload(childValue, fromPath, toPath);
    if (
      nextChildValue
      && !Array.isArray(nextChildValue)
      && typeof nextChildValue === 'object'
      && childInsideFrom
      && nextChildValue.name === childName
    ) {
      nextChildValue = { ...nextChildValue, name: nextChildName };
    }
    remappedSubfolders[nextChildName] = nextChildValue;
  });

  return {
    ...currentFolder,
    path: nextCurrentPath,
    files: remapDocumentFolderPayload(currentFolder.files || [], fromPath, toPath),
    subfolders: remappedSubfolders,
  };
};

export const documentFolderRenameInvalidatesRequestPath = (
  requestPath,
  sourcePath,
  targetPath,
) => {
  const normalizedRequestPath = normalizeDocumentCaseFolderPath(requestPath);
  return isDocumentFolderPathWithin(normalizedRequestPath, sourcePath)
    || isDocumentFolderPathWithin(normalizedRequestPath, targetPath)
    || getDocumentFolderRenameParentPaths(sourcePath, targetPath)
      .includes(normalizedRequestPath);
};

/**
 * Decide whether an async folder response can still mutate cache/view state.
 * Rename mutations invalidate only their source/target subtrees and parent
 * listings, while navigation responses additionally require the same active
 * request and path.
 */
export const isDocumentFolderRequestCurrent = ({
  token,
  mutations = [],
  activePath,
  currentNavigationGeneration,
  requireActiveNavigation = false,
}) => {
  if (!token?.path) return false;

  const invalidated = mutations.some(mutation => (
    String(mutation?.caseId ?? token.caseId) === String(token.caseId)
    &&
    Number(mutation?.generation || 0) > Number(token.mutationGeneration || 0)
    && documentFolderRenameInvalidatesRequestPath(
      token.path,
      mutation.sourcePath,
      mutation.targetPath,
    )
  ));
  if (invalidated) return false;
  if (!requireActiveNavigation) return true;

  return Number(token.navigationGeneration) === Number(currentNavigationGeneration)
    && normalizeDocumentCaseFolderPath(activePath) === normalizeDocumentCaseFolderPath(token.path);
};

export const remapDocumentFolderMetadata = (metadata, sourcePath, targetPath) => (
  Array.from(metadata || []).map((entry) => {
    if (!entry?.file_path || !isDocumentFolderPathWithin(entry.file_path, sourcePath)) {
      return entry;
    }
    return {
      ...entry,
      file_path: remapDocumentStoragePath(entry.file_path, sourcePath, targetPath),
    };
  })
);

export const removeDocumentFolderMetadata = (metadata, folderPath) => (
  Array.from(metadata || []).filter(entry => (
    !entry?.file_path || !isDocumentFolderPathWithin(entry.file_path, folderPath)
  ))
);

const readEntryFile = (entry) => new Promise((resolve, reject) => {
  entry.file(resolve, reject);
});

const readNextDirectoryBatch = (reader) => new Promise((resolve, reject) => {
  reader.readEntries(resolve, reject);
});

const withDocumentUploadRelativePath = (file, relativePath) => {
  if (!file || !relativePath) return file;

  try {
    Object.defineProperty(file, 'webkitRelativePath', {
      configurable: true,
      value: relativePath,
    });
    return file;
  } catch (_) {
    if (typeof File !== 'undefined') {
      const copy = new File([file], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      });
      Object.defineProperty(copy, 'webkitRelativePath', {
        configurable: true,
        value: relativePath,
      });
      return copy;
    }
  }

  return file;
};

const collectEntryFiles = async (entry, basePath = '') => {
  if (!entry) return [];

  const relativePath = basePath ? `${basePath}/${entry.name}` : entry.name;
  if (isDocumentUploadSystemArtifact(relativePath)) return [];

  if (entry.isFile) {
    try {
      const file = await readEntryFile(entry);
      return file ? [basePath ? withDocumentUploadRelativePath(file, relativePath) : file] : [];
    } catch (_) {
      // A stale cloud placeholder or unreadable file must not discard the
      // other readable files in the same dropped directory.
      return [];
    }
  }

  if (!entry.isDirectory) return [];

  let reader;
  try {
    reader = entry.createReader();
  } catch (_) {
    return [];
  }
  const files = [];

  // FileSystemDirectoryReader returns entries in implementation-sized batches
  // (commonly 100 in Chromium), so keep reading until it returns an empty batch.
  while (true) {
    let entries;
    try {
      entries = await readNextDirectoryBatch(reader);
    } catch (_) {
      break;
    }
    if (!entries || entries.length === 0) break;

    const nestedFiles = await Promise.all(
      entries.map(async (childEntry) => {
        try {
          return await collectEntryFiles(childEntry, relativePath);
        } catch (_) {
          return [];
        }
      })
    );
    nestedFiles.forEach((batch) => files.push(...batch));
  }

  return files;
};

export const collectDroppedDocumentFiles = async (dataTransfer) => {
  if (!dataTransfer) return [];

  // FileList and DataTransferItem access is only guaranteed while the drop
  // event is being dispatched. In particular, getAsFile()/webkitGetAsEntry()
  // can start returning null after the first awaited directory/file read.
  // Materialize every live handle before crossing any async boundary.
  let flatFiles = [];
  let items = [];
  try {
    flatFiles = Array.from(dataTransfer.files || []);
  } catch (_) {
    flatFiles = [];
  }
  try {
    items = Array.from(dataTransfer.items || []);
  } catch (_) {
    items = [];
  }

  const sources = items.reduce((captured, item) => {
    if (!item || (item.kind && item.kind !== 'file')) return captured;

    let entry = null;
    let file = null;
    try {
      entry = typeof item.webkitGetAsEntry === 'function'
        ? item.webkitGetAsEntry()
        : null;
    } catch (_) {
      entry = null;
    }
    try {
      file = typeof item.getAsFile === 'function' ? item.getAsFile() : null;
    } catch (_) {
      file = null;
    }

    captured.push({ entry, file });
    return captured;
  }, []);

  if (sources.length === 0) {
    return mergeDocumentUploadFiles(flatFiles);
  }

  const batches = await Promise.all(sources.map(async ({ entry, file }) => {
    if (!entry) return file ? [file] : [];

    const entryFiles = await collectEntryFiles(entry);
    // A direct FileSystemFileEntry can occasionally be stale while its
    // synchronously captured File is still readable (cloud/Outlook drags).
    if (entryFiles.length === 0 && entry.isFile && file) return [file];
    return entryFiles;
  }));
  const collected = batches.flat();

  // Safari and application drags can expose an incomplete DataTransferItemList
  // while still providing a complete FileList. Merge that flat snapshot too,
  // excluding directory placeholder Files already expanded above.
  const directoryNames = new Set(
    sources
      .map(({ entry }) => (entry?.isDirectory ? String(entry.name || '') : ''))
      .filter(Boolean)
  );
  const physicalFileKey = (file) => JSON.stringify([
    String(file?.name || ''),
    Number(file?.size || 0),
    Number(file?.lastModified || 0),
    String(file?.type || ''),
  ]);
  const collectedPhysicalFiles = new Set(collected.map(physicalFileKey));
  const flatFileFallbacks = flatFiles.filter((file) => (
    !directoryNames.has(String(file?.name || ''))
    && !collectedPhysicalFiles.has(physicalFileKey(file))
  ));

  return mergeDocumentUploadFiles(collected, flatFileFallbacks);
};
